import { Add } from "@mui/icons-material";
import { Box, CircularProgress, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { uiVisibleErrorKey } from "../../../utils/errors";
import type { CallInReason } from "../../../utils/models";
import { DraggableList } from "../../UIToolkit/DnD/DraggableList";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { CallInReasonRow, isCallInReasonLabelValid } from "./CallInReasonRow";
import { useCallInReasons, useCreateCallInReason, useReorderCallInReasons } from "./callInReasonsHooks";

const SORT_ORDER_STEP = 10;

export const CallInReasonsConfig: FC = () => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;

	const { data: reasons = [], isLoading } = useCallInReasons();
	const createMutation = useCreateCallInReason();
	const reorderMutation = useReorderCallInReasons();
	const [newLabel, setNewLabel] = useState("");
	// Bumped after a successful Add to remount the label field: it keeps its "has been typed in" state
	// internally, so merely clearing the value would flag the fresh, empty field as an error.
	const [newLabelFieldKey, setNewLabelFieldKey] = useState(0);

	const sortedFromServer = useMemo(() => [...reasons].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)), [reasons]);

	// Local order is what DraggableList renders. We seed it from the server.
	// If the set of ids changes (create / archive) we replace the list outright;
	// otherwise we only patch in fresh objects per id so we keep the user's local
	// drag order — a full replace from the reorder PUT response would snap an
	// in-flight drag back. Patch-by-id also pulls in label edits made on a row.
	const [localOrder, setLocalOrder] = useState<CallInReason[]>(sortedFromServer);
	useEffect(() => {
		const serverIds = sortedFromServer
			.map((r) => r.id)
			.sort()
			.join(",");
		const localIds = localOrder
			.map((r) => r.id)
			.sort()
			.join(",");
		if (serverIds !== localIds) {
			setLocalOrder(sortedFromServer);
			return;
		}
		const byId = new Map(sortedFromServer.map((r) => [r.id, r]));
		let changed = false;
		const merged = localOrder.map((r) => {
			const fresh = byId.get(r.id);
			if (fresh && fresh !== r) {
				changed = true;
				return fresh;
			}
			return r;
		});
		if (changed) setLocalOrder(merged);
	}, [sortedFromServer, localOrder]);

	// Local-only reorder; nothing is persisted until the user clicks Save.
	const handleReorder = (newOrder: CallInReason[]) => setLocalOrder(newOrder);

	// Dirty when the displayed id sequence diverges from what the server gave us.
	const isOrderDirty = useMemo(() => {
		if (localOrder.length !== sortedFromServer.length) return false; // mid-resync; ignore
		return localOrder.some((r, i) => sortedFromServer[i]?.id !== r.id);
	}, [localOrder, sortedFromServer]);

	const handleSaveOrder = async () => {
		// Send all items so the BE has the full snapshot — also covers any pre-existing
		// gaps/duplicates in sort_order that prior single-row edits could have left behind.
		const payload = localOrder.map((reason, index) => ({ reasonId: reason.id, sortOrder: (index + 1) * SORT_ORDER_STEP }));
		try {
			track("call_in_reasons_reorder_save_start", { department_id: departmentId, count: payload.length });
			await reorderMutation.mutateAsync(payload);
			track("call_in_reasons_reorder_save_success", { department_id: departmentId, count: payload.length });
		} catch (e) {
			const message = e instanceof Error ? e.message : undefined;
			trackError("call_in_reasons_reorder_save_error", { department_id: departmentId, error_message: message });
			showSnackbar(t("call.in.reason.save.failed"), "error");
		}
	};

	const handleCancelOrder = () => setLocalOrder(sortedFromServer);

	const handleAdd = async () => {
		const trimmed = newLabel.trim();
		if (!trimmed) {
			showSnackbar(t("call.in.reason.label.empty"), "error");
			return;
		}
		// Place new reason at the bottom of the current order.
		const maxSort = localOrder.reduce((acc, r) => Math.max(acc, r.sortOrder), 0);
		const body = { label: trimmed, sortOrder: maxSort + SORT_ORDER_STEP };
		try {
			track("call_in_reason_create_start", { department_id: departmentId });
			await createMutation.mutateAsync(body);
			track("call_in_reason_create_success", { department_id: departmentId });
			setNewLabel("");
			setNewLabelFieldKey((key) => key + 1);
		} catch (e) {
			const message = e instanceof Error ? e.message : undefined;
			trackError("call_in_reason_create_error", { department_id: departmentId, error_message: message });
			// The backend says why an add was refused (a duplicate name, an empty or too-long label), so show its reason.
			// DEFAULT: any other failure — network, or a refusal without a reason — gets the generic message.
			showSnackbar(t(uiVisibleErrorKey(e) ?? "call.in.reason.save.failed"), "error");
		}
	};

	return (
		<Box component="section" sx={{ display: "flex", flexDirection: "column", gap: "1rem" }} data-testid="call-in-reasons-section">
			<Typography sx={{ color: "#4D5579", fontSize: 13 }}>{t("call.in.reasons.description")}</Typography>

			<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
				<SystemValidationTextField
					key={newLabelFieldKey}
					label={t("call.in.reason.add.label")}
					value={newLabel}
					onChange={(e) => setNewLabel(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") void handleAdd();
					}}
					validation={{
						validationCallback: isCallInReasonLabelValid,
						helperText: {
							invalid: newLabel.trim().length === 0 ? t("call.in.reason.label.empty") : t("call.in.reason.label.too.long"),
						},
						performValidation: "onlyAfterInitialChange",
						showValidWhenValid: "never",
					}}
					reserveHeightForHelperText={false}
					formControlSx={{ width: 280 }}
					injectedProps={{ slotProps: { htmlInput: { "data-testid": "call-in-reason-new-label" } } }}
				/>
				<SystemButton
					onClick={() => void handleAdd()}
					loading={createMutation.isPending}
					disabled={!newLabel.trim()}
					data-testid="call-in-reason-add-button"
					sx={{ height: 40, px: 2 }}
				>
					<Add fontSize="small" />
					{t("add")}
				</SystemButton>
				{isOrderDirty && (
					<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
						<SystemButton
							buttonVariant="secondary"
							onClick={handleCancelOrder}
							disabled={reorderMutation.isPending}
							data-testid="call-in-reasons-order-cancel"
							sx={{ height: 40, px: 2 }}
						>
							{t("cancel")}
						</SystemButton>
						<SystemButton
							onClick={() => void handleSaveOrder()}
							loading={reorderMutation.isPending}
							data-testid="call-in-reasons-order-save"
							sx={{ height: 40, px: 2 }}
						>
							{t("save")}
						</SystemButton>
					</Box>
				)}
			</Box>

			{isLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			) : localOrder.length === 0 ? (
				<Typography sx={{ textAlign: "center", color: "#7681AF", py: 2 }}>{t("call.in.reasons.empty")}</Typography>
			) : (
				<>
					<Typography sx={{ color: "#8B8B8B", fontSize: 11, mt: 0.5 }}>{t("call.in.reasons.drag.hint")}</Typography>
					<Box data-testid="call-in-reasons-list">
						<DraggableList<CallInReason>
							items={localOrder}
							getId={(reason) => reason.id}
							onChange={handleReorder}
							renderItem={({ item, handleListeners }) => <CallInReasonRow reason={item} departmentId={departmentId} handleListeners={handleListeners} />}
						/>
					</Box>
				</>
			)}
		</Box>
	);
};
